const AUTH_STORAGE_KEY = 'mindvrik_auth_user';
const SELECTED_PLAN_KEY = 'mindvrik_mock_selected_plan';
const AUTH_TOKEN_KEY = 'mindvrik_auth_token';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const readJson = (key, fallback) => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

const request = async (path, options = {}) => {
  const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.detail || payload?.message || 'Request failed. Please try again.');
  }
  return payload;
};

const saveUser = (user) => {
  if (user) writeJson(AUTH_STORAGE_KEY, user);
  return user;
};

const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(window.Razorpay);
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.onload = () => resolve(window.Razorpay);
  script.onerror = () => reject(new Error('Could not load Razorpay Checkout. Please check your internet connection.'));
  document.body.appendChild(script);
});

const openRazorpayCheckout = async ({ order, user, plan }) => {
  if (order.keyId === 'development_mode' || order.orderId?.startsWith('dev_order_')) {
    await new Promise((resolve) => setTimeout(resolve, 700));
    return {
      razorpay_order_id: order.orderId,
      razorpay_payment_id: `dev_payment_${Date.now()}`,
      razorpay_signature: 'development_mode',
    };
  }

  const Razorpay = await loadRazorpayCheckout();
  return new Promise((resolve, reject) => {
    const checkout = new Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: 'MindVrik',
      description: plan.name,
      order_id: order.orderId,
      prefill: {
        name: user?.studentName || user?.parentName || '',
        email: user?.email || '',
        contact: user?.mobile || '',
      },
      notes: {
        plan_id: plan.id,
      },
      theme: {
        color: '#2A9D8F',
      },
      handler: resolve,
      modal: {
        ondismiss: () => reject(new Error('Payment was cancelled. Please try again to start the assessment.')),
      },
    });
    checkout.open();
  });
};

export const paymentService = {
  saveSelectedPlan(user, plan) {
    if (!user || !plan) throw new Error('User and plan are required.');
    const selectedPlan = {
      id: plan.id,
      name: plan.name,
      price: plan.price,
      amount: plan.amount,
      features: [...(plan.assessments || []), plan.counseling, ...(plan.features || [])].filter(Boolean),
      selectedAt: new Date().toISOString(),
    };

    writeJson(`${SELECTED_PLAN_KEY}:${user.email}`, selectedPlan);
    saveUser({
      ...user,
      selectedPlan,
      planStatus: 'selected',
      paymentStatus: user.paymentStatus === 'completed' ? 'completed' : 'pending',
    });
    return selectedPlan;
  },

  getSelectedPlan(user) {
    if (!user) return null;
    return readJson(`${SELECTED_PLAN_KEY}:${user.email}`, user.selectedPlan || null);
  },

  async createPaymentOrder({ user, plan }) {
    if (!user || !plan) throw new Error('Missing user or selected plan.');
    return request('/payments/orders', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  async startGatewayPayment({ user, plan, order }) {
    return openRazorpayCheckout({ user, plan, order });
  },

  async verifyPayment(paymentResponse) {
    const result = await request('/payments/verify', {
      method: 'POST',
      body: JSON.stringify(paymentResponse),
    });
    return saveUser(result.user);
  },

  async getPaymentStatus(user) {
    return {
      selectedPlan: this.getSelectedPlan(user),
      paymentStatus: user?.paymentStatus || 'pending',
      paymentDate: user?.paymentDate || null,
    };
  },

  clearSelectedPlan(user) {
    if (!user) return;
    window.localStorage.removeItem(`${SELECTED_PLAN_KEY}:${user.email}`);
    saveUser({
      ...user,
      selectedPlan: null,
      planStatus: 'not_selected',
      paymentStatus: 'pending',
      paymentDate: null,
      paymentId: null,
      assessmentStatus: 'not_started',
    });
  },
};
